import React from 'react';
import { Alert, Button, InputNumber, Select, Tag } from 'antd';
import { CloseOutlined, DeleteOutlined, FilterOutlined, LoadingOutlined, PlusOutlined } from '@ant-design/icons';
import { numberInputProps } from '../../../utils/numberFormat';
import { SmartBetaForm, targetWeights, totalWeight, WEIGHTING_OPTIONS } from '../smartBetaForm';
import { Card, CardTitle, Muted, Row, SubTitle, TotalBar, WeightRow } from '../styles';
import TickerPicker from './TickerPicker';
import ScreenEditor from './ScreenEditor';

interface Props {
  form: SmartBetaForm;
  onChange: (patch: Partial<SmartBetaForm>) => void;
  disabled?: boolean;
  /** The saved basket's last screen pick; omitted while the form has unsaved edits. */
  screened?: string[];
  /** A queued screen is picking the saved basket's tickers. */
  screening?: boolean;
  /** Why the saved basket's last pick failed. */
  screenError?: string;
}

const ScreeningSection: React.FC<Props> = ({ form, onChange, disabled, screened, screening, screenError }) => {
  const used = [...form.whitelist.map((w) => w.symbol), ...form.blacklist];
  const total = totalWeight(form);
  const setRow = (i: number, patch: Partial<SmartBetaForm['whitelist'][number]>) =>
    onChange({
      whitelist: form.whitelist.map((w, j) => (j === i ? { ...w, ...patch } : w)),
    });
  const tickersSum = form.whitelist.reduce((s, w) => s + (w.weight ?? 0), 0);
  const screens = form.criteria.length > 0;
  const autos = form.whitelist.filter((w) => w.weight == null).length;
  const resolved = targetWeights(form);
  const left = Math.round((100 - total) * 100) / 100;
  const method = WEIGHTING_OPTIONS.find((o) => o.value === form.weightingMethod)?.label ?? 'Equal';
  const ok = autos > 0 || screens ? left >= Math.max(autos, 1) * 0.01 : total === 100;
  const sharers = [
    autos > 0 && `${autos} auto ticker${autos > 1 ? 's' : ''}`,
    screens && `up to ${form.screenLimit} screened`,
  ]
    .filter(Boolean)
    .join(' + ');

  return (
    <Card aria-label="Screening criteria">
      <CardTitle>
        <FilterOutlined /> Screening Criteria
      </CardTitle>
      <Muted>Filter the market by price and liquidity; the most liquid matches join the basket as Auto tickers.</Muted>
      <div style={{ marginTop: 8 }}>
        <ScreenEditor form={form} onChange={onChange} disabled={disabled} />
      </div>
      {screening && (
        <Alert
          type="info"
          showIcon
          icon={<LoadingOutlined />}
          style={{ marginTop: 8 }}
          message="Picking the screened tickers…"
          description="The screens run in the background; deposits wait until they finish."
        />
      )}
      {!screening && screenError && (
        <Alert
          type="warning"
          showIcon
          style={{ marginTop: 8 }}
          message="The last screen failed — the pick before it is kept"
          description={screenError}
        />
      )}
      {screened && screened.length > 0 && (
        <Row style={{ marginTop: 8 }} aria-label="Screened tickers">
          <Muted>Picked at the last save or rebalance:</Muted>
          {screened.map((s) => (
            <Tag key={s} color="blue">
              {s}
            </Tag>
          ))}
        </Row>
      )}

      <SubTitle>White List</SubTitle>
      <Muted>
        Tickers to hold. Give a ticker a fixed percent, or leave it empty (Auto) to share what is left by the weighting
        method below. Cash is kept uninvested at each rebalance.
      </Muted>
      <div style={{ marginTop: 8 }}>
        <WeightRow>
          <div style={{ color: '#EAECEF', paddingLeft: 4 }}>Cash</div>
          <InputNumber
            {...numberInputProps()}
            min={0}
            max={100}
            precision={2}
            value={form.cashWeight}
            onChange={(cashWeight) => onChange({ cashWeight })}
            disabled={disabled}
            addonAfter="%"
            aria-label="Cash weight"
          />
          <span />
        </WeightRow>
        {form.whitelist.map((w, i) => (
          <WeightRow key={`${w.symbol}-${i}`}>
            <TickerPicker
              value={w.symbol}
              exclude={used.filter((s) => s !== w.symbol)}
              onPick={(symbol) => setRow(i, { symbol })}
              disabled={disabled}
            />
            <InputNumber
              {...numberInputProps()}
              min={0}
              max={100}
              precision={2}
              value={w.weight}
              // An emptied box parses as 0, and 0 is no fixed weight: either way it means Auto.
              onChange={(weight) => setRow(i, { weight: weight ? weight : null })}
              disabled={disabled}
              addonAfter="%"
              placeholder={w.weight == null && left > 0 && !screens ? `Auto ${resolved[i]?.weight ?? 0}` : 'Auto'}
              aria-label={`${w.symbol || 'Ticker'} weight`}
            />
            <Button
              type="text"
              icon={<DeleteOutlined />}
              onClick={() =>
                onChange({
                  whitelist: form.whitelist.filter((_, j) => j !== i),
                })
              }
              disabled={disabled}
              aria-label={`Remove ${w.symbol || 'row'}`}
              style={{ color: '#848E9C' }}
            />
          </WeightRow>
        ))}
      </div>
      <Row>
        <Button
          type="link"
          size="small"
          icon={<PlusOutlined />}
          style={{ paddingLeft: 0 }}
          disabled={disabled}
          onClick={() =>
            onChange({
              whitelist: [...form.whitelist, { symbol: '', weight: null }],
            })
          }
        >
          Add ticker
        </Button>
        {form.whitelist.some((w) => w.weight != null) && (
          <Button
            type="link"
            size="small"
            disabled={disabled}
            onClick={() =>
              onChange({
                whitelist: form.whitelist.map((w) => ({ ...w, weight: null })),
              })
            }
          >
            Make all auto
          </Button>
        )}
      </Row>
      <TotalBar $ok={ok} role="status">
        {autos > 0 || screens ? (
          <span>
            {ok ? `${left}% left → ${sharers}, ${method.toLowerCase()}` : `Nothing left for the auto tickers`}{' '}
            <Muted>
              (cash {form.cashWeight ?? 0}% + fixed {Math.round(tickersSum * 100) / 100}%)
            </Muted>
          </span>
        ) : (
          <span>
            Total {total}%{' '}
            <Muted>
              (cash {form.cashWeight ?? 0}% + tickers {Math.round(tickersSum * 100) / 100}%)
            </Muted>
          </span>
        )}
        {autos === 0 && !screens && total !== 100 && (
          <Button
            size="small"
            type="link"
            disabled={disabled || tickersSum > 100}
            onClick={() =>
              onChange({
                cashWeight: Math.round((100 - tickersSum) * 100) / 100,
              })
            }
          >
            Put the rest in cash
          </Button>
        )}
      </TotalBar>

      <SubTitle>Black List</SubTitle>
      <Muted>Never bought, even if a future screen picks them.</Muted>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, margin: '8px 0' }}>
        {form.blacklist.map((s) => (
          <Tag
            key={s}
            closable={!disabled}
            closeIcon={<CloseOutlined aria-label={`Remove ${s}`} />}
            onClose={() => onChange({ blacklist: form.blacklist.filter((x) => x !== s) })}
            style={{ padding: '4px 8px', fontSize: 13 }}
          >
            {s}
          </Tag>
        ))}
      </div>
      <TickerPicker
        exclude={used}
        onPick={(s) => onChange({ blacklist: [...form.blacklist, s] })}
        placeholder="Add ticker to black list"
        disabled={disabled}
        clearOnPick
        style={{ maxWidth: 280 }}
      />

      <SubTitle>Weighting Method</SubTitle>
      <Muted>How what is left after cash and the fixed weights is split over the Auto tickers.</Muted>
      <div style={{ marginTop: 8 }}>
        <Select
          value={form.weightingMethod}
          onChange={(weightingMethod) => onChange({ weightingMethod })}
          disabled={disabled}
          style={{ width: 260, maxWidth: '100%' }}
          aria-label="Weighting method"
          options={WEIGHTING_OPTIONS.map((o) => ({
            value: o.value,
            disabled: !o.available,
            label: o.available ? o.label : `${o.label} — coming soon`,
          }))}
        />
      </div>
    </Card>
  );
};

export default ScreeningSection;
